/**
 * The queue's keyboard path: `j` / `k` move the focus over the rows, Enter opens a case,
 * and coming back to the queue puts the focus on the case that was opened.
 *
 * Enter is decided here, not by the browser, because the page listens for Enter on the
 * window: the focused row link opens its own case, a row focused with `j` / `k` whose
 * link lost the focus opens that row, and any other control keeps its own Enter (a
 * button is pressed, a field submits).
 */

/** The element that has the focus, as far as Enter cares. */
export interface ActiveElementInfo {
  /** `data-queue-row` of the focused element: the case its row link opens. */
  rowId: string | null;
  /** True when the focused element is a control with its own Enter (link, button, field). */
  isControl: boolean;
}

const CONTROL_TAGS = new Set(["a", "button", "input", "select", "textarea", "summary"]);
const CONTROL_ROLES = new Set([
  "button",
  "link",
  "checkbox",
  "radio",
  "switch",
  "tab",
  "menuitem",
  "option",
  "combobox",
  "textbox",
  "searchbox",
  "spinbutton",
]);

/** Reads what Enter needs from the focused element (`document.activeElement`). */
export function activeElementInfo(element: Element | null): ActiveElementInfo {
  if (!element || typeof element.tagName !== "string") {
    return { rowId: null, isControl: false };
  }
  const rowId = element.getAttribute("data-queue-row");
  const tag = element.tagName.toLowerCase();
  const role = element.getAttribute("role");
  const isControl =
    CONTROL_TAGS.has(tag) ||
    (role !== null && CONTROL_ROLES.has(role)) ||
    (element as HTMLElement).isContentEditable === true;
  return { rowId: rowId && rowId.trim() !== "" ? rowId : null, isControl };
}

/**
 * The case Enter opens, or null when Enter belongs to another control (or to nothing).
 *
 * - The focused element is a row link (after `j` / `k` or after Tab): its own case.
 * - Focus is on no control (the page body) and a row is focused with `j` / `k`: that row.
 * - Focus is on another control: null, so its own Enter still works.
 */
export function caseToOpenOnEnter(
  active: ActiveElementInfo,
  focusedId: string | null,
  rowIds: readonly string[],
): string | null {
  if (active.rowId) {
    return rowIds.includes(active.rowId) ? active.rowId : null;
  }
  if (active.isControl) {
    return null;
  }
  return focusedId && rowIds.includes(focusedId) ? focusedId : null;
}

export const QUEUE_OPENED_KEY = "i2q.queue.opened";

interface OpenedRow {
  /** The queue's search string (without "?") the case was opened from. */
  search: string;
  id: string;
}

/** Remember which case was opened from which queue view. Fails silently without storage. */
export function rememberOpenedRow(search: string, id: string): void {
  try {
    window.sessionStorage.setItem(QUEUE_OPENED_KEY, JSON.stringify({ search, id }));
  } catch {
    // No storage in this tab: coming back puts the focus nowhere in particular.
  }
}

/**
 * The case to put the focus on when the queue shows `search` again: the one opened from
 * the same view, when it is still on screen. Null otherwise.
 */
export function openedRowToRestore(search: string, rowIds: readonly string[]): string | null {
  let stored: unknown;
  try {
    const raw = window.sessionStorage.getItem(QUEUE_OPENED_KEY);
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
  const value = stored as Partial<OpenedRow> | null;
  if (!value || typeof value.id !== "string" || typeof value.search !== "string") {
    return null;
  }
  return value.search === search && rowIds.includes(value.id) ? value.id : null;
}
